import {
  getObject,
  getBoolean,
  getString,
  getOptionalString,
  getOptionalObject,
} from "@surya-digital/leo-ts-types";
import { RespondentQuestionDetails, RespondentQuestionDetailsDTO } from "./respondentQuestionDetails";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";


export namespace CurrentRespondentScreenDetails {
  export interface CurrentQuestionDTO {
    case: string;
    respondentQuestionDetails: RespondentQuestionDetailsDTO;
    isFirstQuestion: boolean;
    savedAnswerJson?: string | null;
  }
  
  export class CurrentQuestion {
    readonly isCurrentRespondentScreenDetailsCurrentQuestion: boolean = true;
  
    readonly respondentQuestionDetails: RespondentQuestionDetails;
    readonly isFirstQuestion: boolean;
    readonly savedAnswerJson: string | null;
  
  
    constructor(
    respondentQuestionDetails: RespondentQuestionDetails, 
    isFirstQuestion: boolean, 
    savedAnswerJson: string | null = null
    ) {
      this.respondentQuestionDetails = respondentQuestionDetails;
      this.isFirstQuestion = isFirstQuestion;
      if ( savedAnswerJson !== undefined && savedAnswerJson !== null) {
        CurrentQuestion.validateSavedAnswerJson(savedAnswerJson);
      }
      this.savedAnswerJson = savedAnswerJson;
    }
  
    static fromDTO(dto: CurrentQuestionDTO): CurrentQuestion {
      const respondentQuestionDetails = RespondentQuestionDetails.fromDTO(getObject(dto, "respondentQuestionDetails", InvalidCurrentRespondentScreenDetailsCurrentQuestionRespondentQuestionDetailsError));
      const isFirstQuestion = getBoolean(dto, "isFirstQuestion", InvalidCurrentRespondentScreenDetailsCurrentQuestionIsFirstQuestionError);
      const savedAnswerJson = getOptionalString(dto, "savedAnswerJson", InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonError);
      return new CurrentQuestion(
        respondentQuestionDetails,
        isFirstQuestion,
        savedAnswerJson
      );
    }
  
    toDTO(): CurrentQuestionDTO {
      const dto: CurrentQuestionDTO = {
        case: "CURRENT_QUESTION",
          respondentQuestionDetails: this.respondentQuestionDetails.toDTO(),
          isFirstQuestion: this.isFirstQuestion,
      };
      if (this.savedAnswerJson !== undefined && this.savedAnswerJson !== null){
        dto.savedAnswerJson = this.savedAnswerJson;
      }
      return dto;
    }
    private static validateSavedAnswerJson = (savedAnswerJson: string): void => {
      if(savedAnswerJson.trim().length === 0) {
        throw new InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonNonEmptyError(
          `Attribute savedAnswerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  CurrentQuestion.prototype.toString = function toString(): string {
    return `CurrentRespondentScreenDetails.CURRENT_QUESTION(respondentQuestionDetails='${this.respondentQuestionDetails}',isFirstQuestion='${this.isFirstQuestion}',savedAnswerJson='${this.savedAnswerJson}')`;
  };
  
  export interface WelcomePageDTO {
    case: string;
    welcomePageText?: SurveyLocalizedTextDTO | null;
  }
  
  export class WelcomePage {
    readonly isCurrentRespondentScreenDetailsWelcomePage: boolean = true;
  
    readonly welcomePageText: SurveyLocalizedText | null;
  
  
    constructor(
    welcomePageText: SurveyLocalizedText | null = null
    ) {
      this.welcomePageText = welcomePageText;
    }
  
    static fromDTO(dto: WelcomePageDTO): WelcomePage {
      let welcomePageText = getOptionalObject(dto, "welcomePageText");
      if(welcomePageText !== undefined && welcomePageText !== null) {
        welcomePageText = SurveyLocalizedText.fromDTO(getObject(dto, "welcomePageText", InvalidCurrentRespondentScreenDetailsWelcomePageWelcomePageTextError));
      }
      return new WelcomePage(
        welcomePageText
      );
    }
  
    toDTO(): WelcomePageDTO {
      const dto: WelcomePageDTO = {
        case: "WELCOME_PAGE",
      };
      if (this.welcomePageText !== undefined && this.welcomePageText !== null){
        dto.welcomePageText = this.welcomePageText.toDTO();
      }
      return dto;
    }
    }
  
  WelcomePage.prototype.toString = function toString(): string {
    return `CurrentRespondentScreenDetails.WELCOME_PAGE(welcomePageText='${this.welcomePageText}')`;
  };
  
  export interface ThankYouPageDTO {
    case: string;
    thankYouPageText: SurveyLocalizedTextDTO;
  }
  
  export class ThankYouPage {
    readonly isCurrentRespondentScreenDetailsThankYouPage: boolean = true;
  
    readonly thankYouPageText: SurveyLocalizedText;
  
  
    constructor(
    thankYouPageText: SurveyLocalizedText
    ) {
      this.thankYouPageText = thankYouPageText;
    }
  
    static fromDTO(dto: ThankYouPageDTO): ThankYouPage {
      const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidCurrentRespondentScreenDetailsThankYouPageThankYouPageTextError));
      return new ThankYouPage(
        thankYouPageText
      );
    }
  
    toDTO(): ThankYouPageDTO {
      const dto: ThankYouPageDTO = {
        case: "THANK_YOU_PAGE",
          thankYouPageText: this.thankYouPageText.toDTO()
      };
      return dto;
    }
    }
  
  ThankYouPage.prototype.toString = function toString(): string {
    return `CurrentRespondentScreenDetails.THANK_YOU_PAGE(thankYouPageText='${this.thankYouPageText}')`;
  };
  

  export type CurrentRespondentScreenDetails = CurrentQuestion | WelcomePage | ThankYouPage;

  export type CurrentRespondentScreenDetailsDTO = CurrentQuestionDTO | WelcomePageDTO | ThankYouPageDTO;

  export const fromDTO = (dto: CurrentRespondentScreenDetailsDTO): CurrentRespondentScreenDetails => {
    switch (dto.case) {
      case "CURRENT_QUESTION":
          return CurrentQuestion.fromDTO(dto as CurrentQuestionDTO);
      case "WELCOME_PAGE":
          return WelcomePage.fromDTO(dto as WelcomePageDTO);
      case "THANK_YOU_PAGE":
          return ThankYouPage.fromDTO(dto as ThankYouPageDTO);
      default:
        throw new InvalidCurrentRespondentScreenDetailsError(
            `Case ${dto.case} is not valid case of CurrentRespondentScreenDetails.`
        );
    }
  };
}

export class InvalidCurrentRespondentScreenDetailsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsError";
  }
}
export class InvalidCurrentRespondentScreenDetailsCurrentQuestionError extends InvalidCurrentRespondentScreenDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsCurrentQuestionError";
  }
}

export class InvalidCurrentRespondentScreenDetailsCurrentQuestionRespondentQuestionDetailsError extends InvalidCurrentRespondentScreenDetailsCurrentQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsCurrentQuestionRespondentQuestionDetailsError";
  }
}

export class InvalidCurrentRespondentScreenDetailsCurrentQuestionIsFirstQuestionError extends InvalidCurrentRespondentScreenDetailsCurrentQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsCurrentQuestionIsFirstQuestionError";
  }
}

export class InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonError extends InvalidCurrentRespondentScreenDetailsCurrentQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonError";
  }
}

export class InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonNonEmptyError extends InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsCurrentQuestionSavedAnswerJsonNonEmptyError";
  }
}

export class InvalidCurrentRespondentScreenDetailsWelcomePageError extends InvalidCurrentRespondentScreenDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsWelcomePageError";
  }
}

export class InvalidCurrentRespondentScreenDetailsWelcomePageWelcomePageTextError extends InvalidCurrentRespondentScreenDetailsWelcomePageError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsWelcomePageWelcomePageTextError";
  }
}

export class InvalidCurrentRespondentScreenDetailsThankYouPageError extends InvalidCurrentRespondentScreenDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsThankYouPageError";
  }
}

export class InvalidCurrentRespondentScreenDetailsThankYouPageThankYouPageTextError extends InvalidCurrentRespondentScreenDetailsThankYouPageError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidCurrentRespondentScreenDetailsThankYouPageThankYouPageTextError";
  }
}
